import { AlertSeverity } from '@rover/react-lib/src/components/notifications/Alert/Alert';
import {
  AvailableForSelectedDatesEnum,
  OptimizedSearchResult,
} from '@rover/rsdk/src/apiClient/latest';
import { PriceTransparencyLevels } from '@rover/types/src/PriceTransparencyLevel';

import {
  AlertContentEnum,
  getAlertContent,
  getLiteAlertContentType,
  getSubHeader,
  HEADER_TITLES,
  HeaderIconEnum,
  HeaderTitlesEnum,
  SubHeadersEnum,
} from './constants';
import { BestMatchBuilderProps, PrebuiltHeaderProps } from './types';

interface Props extends BestMatchBuilderProps {
  hasPets: boolean;
  isBestMatchExperience?: boolean;
  isBestMatchesSearch?: boolean;
  isInitialPage?: boolean;
  isSeoPage?: boolean;
  limitTopResults: boolean;
  selectedProvider?: OptimizedSearchResult;
  isBHUser?: boolean;
}

const buildMainSearchResultsHeader = (props: Props): PrebuiltHeaderProps | object | null => {
  const { startDate, endDate } = props.dateData;
  const availability =
    props.selectedProvider?.availableForSelectedDates || AvailableForSelectedDatesEnum.unknown;
  const hasDates = !!startDate && !!endDate;
  const hasSelectedProvider = !!props.selectedProvider;
  const isSelectedProviderAvailable = availability === AvailableForSelectedDatesEnum.available;
  const { isBHUser } = props;

  if (
    !hasSelectedProvider &&
    props.priceTransparencyLevel === PriceTransparencyLevels.none &&
    !isBHUser &&
    !props.isBestMatchExperience &&
    !props.limitTopResults &&
    hasDates &&
    props.hasPets
  ) {
    return null;
  }

  let icon: HeaderIconEnum | undefined;
  let title: string | undefined;
  let primarySubheader: string | undefined;
  let alertText: string | null | undefined;
  let isBestMatchHeader = false;

  // For SEO pages
  const handleSeoPage = (): void => {
    icon = HeaderIconEnum.SEARCH;
    title = HEADER_TITLES()[HeaderTitlesEnum.FIND_MATCH];

    switch (props.priceTransparencyLevel) {
      case PriceTransparencyLevels.full:
        alertText = getAlertContent(AlertContentEnum.PRICE_TRANSPARENCY_FULL_SEO_PAGE, props);
        break;
      case PriceTransparencyLevels.lite:
        alertText = getAlertContent(AlertContentEnum.PRICE_TRANSPARENCY_LITE_SEO_PAGE, props);
        break;
      default:
        break;
    }
  };

  // For scenarios where dates are not selected
  const handleWithNoDates = (): void => {
    icon = HeaderIconEnum.SEARCH;
    title = HEADER_TITLES()[HeaderTitlesEnum.FIND_MATCH];
    if (isBHUser) {
      alertText = getAlertContent(AlertContentEnum.PRICE_TRANSPARENCY_FULL_ADD_DATES, props);
    } else {
      const petCount = props.petCount || 0;
      switch (props.priceTransparencyLevel) {
        case PriceTransparencyLevels.full:
          alertText = hasSelectedProvider
            ? null
            : getAlertContent(AlertContentEnum.PRICE_TRANSPARENCY_FULL_ADD_DATES, props);
          break;
        case PriceTransparencyLevels.lite:
          if (hasSelectedProvider) {
            alertText = null;
          } else {
            const contentType = getLiteAlertContentType(
              false,
              petCount,
              props.isPetCountersExperiment
            );
            alertText = getAlertContent(contentType, props);
          }
          break;
        case PriceTransparencyLevels.none:
        default:
          primarySubheader = getSubHeader(SubHeadersEnum.ADD_DATES, props);
          break;
      }
    }
  };

  // For scenarios that show pricing with dates
  const handlePricesForUnit = (): void => {
    if (isBHUser) {
      alertText = getAlertContent(AlertContentEnum.PRICE_TRANSPARENCY_FULL_PRICES_FOR_UNIT, props);
    } else {
      const petCount = props.petCount || 0;
      switch (props.priceTransparencyLevel) {
        case PriceTransparencyLevels.full:
          alertText = hasSelectedProvider
            ? null
            : getAlertContent(AlertContentEnum.PRICE_TRANSPARENCY_FULL_PRICES_FOR_UNIT, props);
          break;
        case PriceTransparencyLevels.lite:
          if (hasSelectedProvider) {
            alertText = null;
          } else {
            const contentType = getLiteAlertContentType(
              true,
              petCount,
              props.isPetCountersExperiment
            );
            alertText = getAlertContent(contentType, props);
          }
          break;
        case PriceTransparencyLevels.none:
        default:
          primarySubheader = getSubHeader(SubHeadersEnum.SEEING_PROVIDERS_DATES, props);
          break;
      }
    }
  };

  // For selected provider scenarios
  const handleWithSelectedProvider = (): void => {
    icon = HeaderIconEnum.MAP;

    if (props.isBestMatchExperience) {
      if (!hasDates) {
        handleWithNoDates();
        return;
      }

      if (props.isBestMatchesSearch && !props.hasBestMatches) {
        // If it's a best matches search, then this header title will be based on
        // the absence of best match results.
        title = HEADER_TITLES()[HeaderTitlesEnum.SITTERS_IN_AREA];
      } else if (!props.hasBestMatches) {
        if (props.isInitialPage) {
          // This header renders if there are no best match results, regardless of
          // whether it's a best matches search.
          icon = HeaderIconEnum.BOLT;
          title = HEADER_TITLES()[HeaderTitlesEnum.BEST_MATCHES];
          isBestMatchHeader = true;
        } else {
          title = HEADER_TITLES()[HeaderTitlesEnum.OTHER_SITTERS_IN_AREA];
          primarySubheader = getSubHeader(SubHeadersEnum.SHOWING_OTHER_PROVIDERS, props);

          return;
        }
      }

      handlePricesForUnit();
      return;
    }

    if (props.priceTransparencyLevel !== PriceTransparencyLevels.none) {
      title = HEADER_TITLES()[HeaderTitlesEnum.OTHER_SITTERS_IN_AREA];

      if (hasDates) {
        primarySubheader = getSubHeader(SubHeadersEnum.SHOWING_OTHER_PROVIDERS, props);
      }

      return;
    }

    if (hasDates && !isSelectedProviderAvailable) {
      title = HEADER_TITLES()[HeaderTitlesEnum.AVAILABLE_SITTERS_NEAR_YOU];
      primarySubheader = getSubHeader(SubHeadersEnum.POST_SELECTED_SITTER_RESULTS, props);

      return;
    }

    title = HEADER_TITLES()[HeaderTitlesEnum.SITTERS_CLOSE_TO_YOU];

    if (hasDates) {
      primarySubheader = getSubHeader(SubHeadersEnum.POST_SELECTED_SITTER_RESULTS, props);
    } else {
      primarySubheader = getSubHeader(SubHeadersEnum.ADD_DATES, props);
    }
  };

  // For the rest of the search results, regardless of best matches
  const handleStandardHeaders = (): void => {
    icon = HeaderIconEnum.MAP;

    if (props.isBestMatchExperience) {
      if (props.isBestMatchesSearch) {
        if (props.hasBestMatches) {
          title = HEADER_TITLES()[HeaderTitlesEnum.OTHER_SITTERS_IN_AREA];
          primarySubheader = getSubHeader(SubHeadersEnum.SHOWING_OTHER_PROVIDERS, props);

          return;
        }

        // only if no results
        title = HEADER_TITLES()[HeaderTitlesEnum.SITTERS_IN_AREA];
      } else if (props.hasBestMatches || (!props.isBestMatchesSearch && !props.isInitialPage)) {
        title = HEADER_TITLES()[HeaderTitlesEnum.OTHER_SITTERS_IN_AREA];
        primarySubheader = getSubHeader(SubHeadersEnum.SHOWING_OTHER_PROVIDERS, props);

        return;
      } else {
        icon = HeaderIconEnum.BOLT;
        title = HEADER_TITLES()[HeaderTitlesEnum.BEST_MATCHES];
        isBestMatchHeader = true;
      }
    } else {
      title = HEADER_TITLES()[HeaderTitlesEnum.SITTERS_IN_AREA];
    }

    handlePricesForUnit();
  };

  // Main decision logic
  if (props.limitTopResults) {
    icon = HeaderIconEnum.SEARCH;
    title = HEADER_TITLES()[HeaderTitlesEnum.OTHER_SITTERS_IN_AREA];
  } else {
    if (hasSelectedProvider) {
      handleWithSelectedProvider();
    } else if (props.isSeoPage) {
      if (props.priceTransparencyLevel === PriceTransparencyLevels.none) {
        return null;
      }
      handleSeoPage();
    } else if (!hasDates) {
      handleWithNoDates();
    }

    if (!title) {
      handleStandardHeaders();
    }
  }

  return {
    icon,
    title,
    primarySubheader,
    alertText,
    alertSeverity: startDate && endDate ? AlertSeverity.INFO : AlertSeverity.WARNING,
    isBestMatchHeader,
  };
};

export default buildMainSearchResultsHeader;
